import React, { useContext, useState, useEffect } from 'react';
import {
  AppHeader,
  IconButton,
  Icon,
  LiveRegion,
  useLiveRegionContext
} from '@sumtotal/react-foundation';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import Config from '../../../config/Config';
import ProfileMenu from './ProfileMenu';
import CurrentThemeContext from '../../../contexts/ThemeContext';
import DomainSwitcher from './DomainSwitcher';
import Help from './Help';
import EnterpriseSearch from './EnterpriseSearch';
import ShoppingCart from './ShoppingCart';
import useUserContext from '../../hooks/useUserContext';
import authService from '../../../authentication/AuthorizeService';
import getLogger from '../../../utils/logger';
import {
  addIframeClickListeners,
  removeIframeClickListeners
} from '../../../modules/core/services/coreIFrameHelper';
import useHeaderContext from '../../hooks/useHeaderContext';

const SearchIcon = styled(IconButton)`
  color: ${(props) => props.theme.variables.headerBarColor};
  &.Mui-disabled {
    color: ${(props) => props.theme.variables.headerBarColor};
    opacity: 0.5;
  }
`;
const CloseIcon = styled(IconButton)`
  padding: 0rem 0rem 0rem 0.5rem;
  color: ${(props) =>
    props.isFloatingSearch
      ? props.theme.variables.leftNavParentColor
      : props.theme.variables.headerBarColor};
  &.Mui-disabled {
    color: ${(props) =>
      props.isFloatingSearch
        ? props.theme.variables.leftNavParentColor
        : props.theme.variables.headerBarColor};
    opacity: 0.5;
  }
`;

const SearchContainer = styled.div`
  display: ${(props) => (props.showGrid ? 'grid' : 'block')};
  display: ${(props) => (props.showGrid ? '-ms-grid' : 'block')};
  grid-template-columns: ${(props) => (props.showGrid ? '1fr auto' : '')};
  -ms-grid-columns: ${(props) => (props.showGrid ? '1fr auto' : '')};
  .gridColumn1 {
    -ms-grid-column: 1;
  }
  .gridColumn2 {
    -ms-grid-column: 2;
  }
`;

const AppMenuHeader = ({
  menusData,
  userData,
  profileMenus,
  helpMenu,
  logoClickUrl,
  domainSwitchNode,
  searchTypes,
  searchUrl,
  shoppingCartMenu,
  onNavigationCallback
}) => {
  const { t } = useTranslation([
    'TRANSLATIONS_RCORE_TOPNAV',
    'TRANSLATIONS_RCORE_COMMON',
    'TRANSLATIONS_RCORE_TOPNAV_SEARCH'
  ]);
  const translationsData = {};
  // Pass all the translation keys required for AppHeader component
  const translationKeys = [
    'RCORE_TOPNAV_MENU_QUICKFIND_SEARCH',
    'RCORE_TOPNAV_MENU_LABEL_CLOSE_MENU',
    'RCORE_TOPNAV_MENU_LABEL_SEARCH',
    'TRANSLATIONS_RCORE_TOPNAV_SEARCH:RCORE_TOPNAV_SEARCH_CLEAR_TEXT',
    'RCORE_TOPNAV_MY_TEAM_MENU_CLOSE',
    'RCORE_TOPNAV_SELF_MENU_CLOSE',
    'RCORE_TOPNAV_ADMINISTRATION_MENU_CLOSE',
    'RCORE_TOPNAV_ADMINISTRATION_MENU_SECTION',
    'RCORE_TOPNAV_MY_TEAM_MENU_SECTION',
    'RCORE_TOPNAV_SELF_MENU_SECTION',
    'RCORE_TOPNAV_MENU_AREAS',
    'RCORE_TOPNAV_MENU_QUICKFIND_SEARCH_NODATA'
  ];
  translationKeys.forEach((key) => {
    translationsData[key] = t(key);
  });
  const screenResolution = window.matchMedia('(max-width: 1279px)');
  const [smallerResolution, setSmallerResolution] = useState(screenResolution.matches);
  const [searchIconSelected, setSearchIconSelected] = useState(false);
  function screenResize(e) {
    setSmallerResolution(e.matches);
    if (searchIconSelected) setSearchIconSelected(false);
  }
  const { setLiveRegionMessage } = useLiveRegionContext();
  const themeContext = useContext(CurrentThemeContext);
  const { user } = useUserContext();
  const disableAppHeader = user.isEmulatedUser ? false : user.mustAnswerSecurityQuestions;
  const { logoData, favIconData } = themeContext;
  const [previewLogoUrl] = logoData;
  const [favIconUrl] = favIconData;
  const headerData = useHeaderContext();

  let LogoSrc = `${Config.domain}//${Config.logo}`;
  if (previewLogoUrl && previewLogoUrl !== '') {
    LogoSrc = previewLogoUrl;
  }

  let favIconSrc = `${Config.domain}${Config.favicon}`;
  if (favIconUrl && favIconUrl !== '') {
    favIconSrc = favIconUrl;
  }
  const faviconElement = document.getElementById('favicon');
  if (faviconElement) {
    faviconElement.href = favIconSrc;
  }

  const userDetails = { ...userData };
  if (userDetails.personInfo.imagePath !== user.image) {
    userDetails.personInfo.imagePath = user.image;
  } else {
    if (userDetails.personInfo.fullName !== user.name) {
      userDetails.personInfo.fullName = user.name;
    }
    if (userDetails.jobTitle !== user.jobTitle) {
      userDetails.jobTitle = user.jobTitle;
    }
  }
  const handleSearchClick = () => {
    setLiveRegionMessage('');
    setSearchIconSelected(true);
    setTimeout(() => {
      const focusElement = document.getElementById('enterpriseSearchComboBox');
      if (focusElement) {
        focusElement.focus();
      }
      setLiveRegionMessage(t('RCORE_TOPNAV_NAVIGATION_MENUS'));
    }, 1000);
  };
  const handleSearchClose = () => {
    setSearchIconSelected(false);

    setTimeout(() => {
      const focusElement = document.getElementsByClassName('headerSearchIcon');
      if (focusElement) {
        focusElement[0].focus();
      }
    });
  };

  useEffect(() => {
    screenResolution.addListener(screenResize);
    return () => screenResolution.removeListener(screenResize);
  });

  const onMenuClick = (selectedNode) => {
    removeIframeClickListeners();
    onNavigationCallback(selectedNode);
    if (selectedNode && selectedNode.nodeKey === 'self_createaccount') {
      authService
        .removeAccessToken()
        .then(() => {
          window.location.assign(selectedNode.url);
          return null;
        })
        .catch((err) => {
          getLogger().error(err);
        });
    }
  };

  const onMenuOpen = (callbackForClose) => {
    if (headerData.headerContext.coreFrameLoaded) {
      setTimeout(() => {
        addIframeClickListeners(callbackForClose);
      }, 100);
    }
  };
  const onSkipLinkClick = () => {
    const contentElement = document.getElementsByClassName('content')[0];
    contentElement.setAttribute('tabindex', '-1');
    contentElement.focus();
  };

  const readResults = (count, searchText) => {
    setLiveRegionMessage('');
    setTimeout(() => {
      setLiveRegionMessage(
        t('TRANSLATIONS_RCORE_COMMON:RCORE_TOPNAV_SEARCH_RESULTS_ANNOUNCEMENT_TEXT', {
          SearchCount: count,
          SearchText: searchText
        })
      );
    }, 500);
  };

  return (
    <>
      <LiveRegion />
      <div id="modal-header-backdrop" className="modal-backdrop header-backdrop" />
      <AppHeader
        disableAppHeader={disableAppHeader}
        logoSrc={LogoSrc}
        skipLinkText={t('RCORE_TOPNAV_SKIP_LINK')}
        skipMenufocus={onSkipLinkClick}
        logoClickUrl={logoClickUrl}
        menusData={menusData}
        hideMenus={searchIconSelected}
        translations={translationsData}
        onMenuOpen={onMenuOpen}
        onMenuClose={onMenuClick}
        poperLeftPosition={93}
        poperTopPosition={52}
        alt={t('TRANSLATIONS_RCORE_COMMON:RCORE_HOME_TEXT')}
        ResultsCallback={readResults}
        searchElement={
          searchTypes &&
          searchTypes.length > 0 &&
          searchUrl &&
          (smallerResolution && !searchIconSelected ? (
            <SearchIcon
              className="headerSearchIcon"
              label={t('RCORE_TOPNAV_SEARCH_SHOW_TEXT')}
              color="inherit"
              disabled={disableAppHeader}
              onClick={handleSearchClick}
              disableRipple={false}
              disableFocusRipple={false}
            >
              <Icon iconType="MaterialIcon" iconName="Search" size={1.75} />
            </SearchIcon>
          ) : (
            searchTypes &&
            searchTypes.length > 0 && (
              <SearchContainer showGrid={searchIconSelected}>
                <EnterpriseSearch
                  className="gridColumn1"
                  searchTypes={searchTypes}
                  searchUrl={searchUrl}
                  disableSearch={disableAppHeader}
                  isFloatingSearch={searchIconSelected}
                />
                {searchIconSelected && (
                  <CloseIcon
                    className="gridColumn2"
                    label={t('RCORE_TOPNAV_SEARCH_HIDE_TEXT')}
                    color="inherit"
                    onClick={handleSearchClose}
                    disableRipple={false}
                    disableFocusRipple={false}
                    isFloatingSearch={searchIconSelected}
                  >
                    <Icon iconType="MaterialIcon" iconName="Close" size={1.5} />
                  </CloseIcon>
                )}
              </SearchContainer>
            )
          ))
        }
        shoppingCartElement={
          shoppingCartMenu && <ShoppingCart shoppingCartMenu={shoppingCartMenu} />
        }
        profileMenu={<ProfileMenu userData={userDetails} profileMenus={profileMenus} />}
        helpElement={helpMenu && <Help helpMenu={helpMenu} />}
        adminHeaderCustomElement={
          domainSwitchNode && (
            <DomainSwitcher
              domainName={domainSwitchNode.name}
              domainSwitchUrl={domainSwitchNode.url}
            />
          )
        }
      />
    </>
  );
};

AppMenuHeader.propTypes = {
  /**
   * Menus data
   */
  menusData: PropTypes.arrayOf(PropTypes.shape({})),
  /**
   * user data
   */
  userData: PropTypes.oneOfType([PropTypes.object]),
  /**
   * Profile Menus
   */
  profileMenus: PropTypes.arrayOf(PropTypes.shape({})),
  /**
   * Help Menu
   */
  helpMenu: PropTypes.oneOfType([PropTypes.object]),
  /**
   * Home Logo click url
   */
  logoClickUrl: PropTypes.string,
  /**
   * Domain switch node
   */
  domainSwitchNode: PropTypes.oneOfType([PropTypes.object]),
  /**
   * Enabled search types
   */
  searchTypes: PropTypes.arrayOf(PropTypes.shape({})),
  /**
   * Search Url
   */
  searchUrl: PropTypes.string,
  /**
   * ShoppingCart Menu
   */
  shoppingCartMenu: PropTypes.oneOfType([PropTypes.object]),
  /**
   * Callback fired when menuItem is clicked.
   */
  onNavigationCallback: PropTypes.func
};

AppMenuHeader.defaultProps = {
  menusData: undefined,
  userData: undefined,
  profileMenus: undefined,
  helpMenu: undefined,
  logoClickUrl: undefined,
  domainSwitchNode: undefined,
  searchTypes: undefined,
  searchUrl: '',
  shoppingCartMenu: undefined,
  onNavigationCallback: undefined
};

export default AppMenuHeader;
